import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { CacheStatusBar } from './OfflineComponents';
import {
  LayoutDashboard,
  Package,
  ScanBarcode,
  ArrowLeftRight,
  ClipboardList,
  Building2,
  FolderTree,
  FileBarChart,
  Users,
  Menu,
  X,
  LogOut,
  ChevronDown,
  User,
  BookOpen,
  Upload,
  Tags,
  HelpCircle,
  GraduationCap,
  Settings,
  Globe,
  Coins
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { toast } from 'sonner';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, isAdmin, isGestionnaire } = useAuth();

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/furniture', icon: Package, label: 'Mobilier' },
    { to: '/catalog', icon: BookOpen, label: 'Catalogue' },
    { to: '/furniture-types', icon: Tags, label: 'Types mobilier' },
    { to: '/scan', icon: ScanBarcode, label: 'Scanner' },
    { to: '/movements', icon: ArrowLeftRight, label: 'Mouvements' },
    { to: '/inventory', icon: ClipboardList, label: 'Inventaire' },
    { to: '/locations', icon: FolderTree, label: 'Localisations' },
    { to: '/import', icon: Upload, label: 'Import CSV' },
    { to: '/reports', icon: FileBarChart, label: 'Rapports' },
    { to: '/documentation', icon: HelpCircle, label: 'Documentation' },
    { to: '/settings', icon: Settings, label: 'Paramètres' },
  ];

  if (isAdmin()) {
    navItems.push({ to: '/users', icon: Users, label: 'Utilisateurs' });
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''} md:translate-x-0`}>
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight">GestMob</h1>
                <p className="text-xs text-slate-400">Gestion Mobilier</p>
              </div>
            </div>
            <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2 text-slate-300">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
              <User className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.prenom} {user?.nom}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const BottomNav = () => {
  const { isAdmin } = useAuth();
  
  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/furniture', icon: Package, label: 'Mobilier' },
    { to: '/scan', icon: ScanBarcode, label: 'Scanner' },
    { to: '/movements', icon: ArrowLeftRight, label: 'Mouvements' },
    { to: '/inventory', icon: ClipboardList, label: 'Inventaire' },
  ];

  return (
    <nav className="bottom-nav">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 px-3 py-2 ${
              isActive ? 'text-blue-600' : 'text-slate-500'
            }`
          }
        >
          <item.icon className="w-5 h-5" />
          <span className="text-xs">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { startTutorial } = useTutorial();
  const { language, currency, languages, currencies, updateLanguage, updateCurrency } = usePreferences();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStartTutorial = () => {
    navigate('/dashboard');
    setTimeout(() => {
      startTutorial();
    }, 100);
  };

  const handleLanguageChange = async (newLanguage) => {
    const success = await updateLanguage(newLanguage);
    if (success) {
      toast.success(language === 'fr' ? 'Langue mise à jour' : 'Language updated');
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    const success = await updateCurrency(newCurrency);
    if (success) {
      toast.success(language === 'fr' ? 'Devise mise à jour' : 'Currency updated');
    }
  };

  const getCurrencySymbol = (code) => {
    const curr = currencies.find(c => c.code === code);
    return curr?.symbol || code;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="main-content">
        {/* Top Header */}
        <header className="sticky top-0 z-20 glass-effect border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1" />

            {/* Tutorial Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartTutorial}
              className="mr-3 hidden sm:flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="tutorial-help-btn"
            >
              <GraduationCap className="w-4 h-4" />
              <span>Guide</span>
            </Button>

            {/* Cache Status Indicator */}
            <CacheStatusBar className="mr-4" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-600" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">
                    {user?.prenom} {user?.nom}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.prenom} {user?.nom}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <p className="text-xs text-blue-600 capitalize mt-1">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                
                {/* Quick Language Selector */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <Globe className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Langue' : 'Language'}
                    <span className="ml-auto text-xs text-slate-500">
                      {language === 'fr' ? '🇫🇷' : '🇬🇧'}
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup value={language} onValueChange={handleLanguageChange}>
                      {languages.map((lang) => (
                        <DropdownMenuRadioItem key={lang.code} value={lang.code} className="cursor-pointer">
                          {lang.code === 'fr' ? '🇫🇷' : '🇬🇧'} {lang.name}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {/* Quick Currency Selector */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <Coins className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Devise' : 'Currency'}
                    <span className="ml-auto text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                      {getCurrencySymbol(currency)}
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup value={currency} onValueChange={handleCurrencyChange}>
                      {currencies.map((curr) => (
                        <DropdownMenuRadioItem key={curr.code} value={curr.code} className="cursor-pointer">
                          <span className="font-mono text-xs mr-2">{curr.symbol}</span>
                          {curr.name}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Paramètres' : 'Settings'}
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-rose-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Déconnexion' : 'Logout'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      <BottomNav />
    </div>
  );
};

export default Layout;
